import React from "react";

import { cloneScenario, createCustomLineItem, createInitialIntake, createScenario } from "@features/real-estate/state/constants";
import type {
  RealEstateEnrichmentResponse,
  RealEstateEnrichmentState,
  RealEstateIntake,
  RealEstateLineItem,
  RealEstateScenario,
  RealEstateWorkspaceState,
} from "@features/real-estate/types";

type ScenarioSection = keyof Omit<RealEstateScenario, "id" | "name" | "lineItems">;

type UpdateScenarioFieldAction = {
  type: "update_scenario_field";
  scenarioId: RealEstateScenario["id"];
  section: ScenarioSection;
  field: string;
  value: number;
};

type WorkspaceAction =
  | { type: "update_intake"; patch: Partial<RealEstateIntake>; }
  | { type: "apply_intake_to_base"; }
  | { type: "set_redirect_notice"; notice: string | null; }
  | { type: "set_enrichment_state"; enrichmentState: RealEstateEnrichmentState; error?: string | null; response?: RealEstateEnrichmentResponse | null; }
  | UpdateScenarioFieldAction
  | { type: "update_line_item"; scenarioId: RealEstateScenario["id"]; lineItemId: string; patch: Partial<RealEstateLineItem>; }
  | { type: "add_line_item"; scenarioId: RealEstateScenario["id"]; section: RealEstateLineItem["section"]; }
  | { type: "remove_line_item"; scenarioId: RealEstateScenario["id"]; lineItemId: string; }
  | { type: "clone_base_scenario"; scenarioId: "upside" | "downside"; name: "Upside" | "Downside"; }
  | { type: "reset_workspace"; };

const initialIntake = createInitialIntake();

export const initialWorkspaceState: RealEstateWorkspaceState = {
  intake: initialIntake,
  enrichment: null,
  enrichmentState: "idle",
  enrichmentError: null,
  redirectNotice: null,
  scenarios: [createScenario("Base", "base", initialIntake)],
};

export function workspaceReducer(state: RealEstateWorkspaceState, action: WorkspaceAction): RealEstateWorkspaceState {
  switch (action.type) {
    case "update_intake":
      return {
        ...state,
        intake: {
          ...state.intake,
          ...action.patch,
        },
      };
    case "apply_intake_to_base":
      return {
        ...state,
        scenarios: state.scenarios.map((scenario) => {
          if (scenario.id !== "base") {
            return scenario;
          }
          return {
            ...scenario,
            financing: {
              ...scenario.financing,
              purchasePrice: state.intake.targetOffer > 0 ? state.intake.targetOffer : state.intake.askingPrice,
            },
            operatingExpenses: {
              ...scenario.operatingExpenses,
              annualPropertyTax: state.intake.annualPropertyTax,
              annualInsurance: state.intake.annualInsurance,
            },
          };
        }),
      };
    case "set_redirect_notice":
      return {
        ...state,
        redirectNotice: action.notice,
      };
    case "set_enrichment_state":
      return {
        ...state,
        enrichmentState: action.enrichmentState,
        enrichmentError: action.error ?? null,
        enrichment: action.response !== undefined ? action.response : state.enrichment,
      };
    case "update_scenario_field":
      return {
        ...state,
        scenarios: state.scenarios.map((scenario) => {
          if (scenario.id !== action.scenarioId) {
            return scenario;
          }
          const section = scenario[action.section] as Record<string, number>;
          return {
            ...scenario,
            [action.section]: {
              ...section,
              [action.field]: action.value,
            },
          };
        }),
      };
    case "update_line_item":
      return {
        ...state,
        scenarios: state.scenarios.map((scenario) => {
          if (scenario.id !== action.scenarioId) {
            return scenario;
          }
          return {
            ...scenario,
            lineItems: scenario.lineItems.map((item) => (
              item.id === action.lineItemId
                ? { ...item, ...action.patch }
                : item
            )),
          };
        }),
      };
    case "add_line_item":
      return {
        ...state,
        scenarios: state.scenarios.map((scenario) => (
          scenario.id === action.scenarioId
            ? { ...scenario, lineItems: [...scenario.lineItems, createCustomLineItem(action.section)] }
            : scenario
        )),
      };
    case "remove_line_item":
      return {
        ...state,
        scenarios: state.scenarios.map((scenario) => (
          scenario.id === action.scenarioId
            ? { ...scenario, lineItems: scenario.lineItems.filter((item) => item.id !== action.lineItemId || !item.removable) }
            : scenario
        )),
      };
    case "clone_base_scenario": {
      if (state.scenarios.some((scenario) => scenario.id === action.scenarioId)) {
        return state;
      }
      const baseScenario = state.scenarios.find((scenario) => scenario.id === "base");
      if (!baseScenario) {
        return state;
      }
      return {
        ...state,
        scenarios: [...state.scenarios, cloneScenario(baseScenario, action.name, action.scenarioId)],
      };
    }
    case "reset_workspace":
      return initialWorkspaceState;
    default:
      return state;
  }
}

type WorkspaceContextValue = {
  state: RealEstateWorkspaceState;
  dispatch: React.Dispatch<WorkspaceAction>;
};

const WorkspaceContext = React.createContext<WorkspaceContextValue | null>(null);

export function RealEstateWorkspaceProvider({ children }: { children: React.ReactNode; }): React.ReactElement {
  const [state, dispatch] = React.useReducer(workspaceReducer, initialWorkspaceState);
  const value = React.useMemo(() => ({ state, dispatch }), [state]);
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useRealEstateWorkspace(): WorkspaceContextValue {
  const context = React.useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useRealEstateWorkspace must be used inside RealEstateWorkspaceProvider");
  }
  return context;
}

export function hasActiveWorkspace(state: RealEstateWorkspaceState): boolean {
  return state.intake.addressLine1.trim() !== "" && state.intake.city.trim() !== "" && state.intake.state.trim() !== "";
}

export function getBaseScenario(state: RealEstateWorkspaceState): RealEstateScenario {
  return state.scenarios.find((scenario) => scenario.id === "base") || state.scenarios[0];
}
